<template>
  <nav>
    <v-row
      class="fill-height"
      align="center"
      v-if="!loadingData"
    >
      <v-col
        cols="12"
        sm="8"
        md="6"
        offset-sm="2"
        offset-md="3"
        align="center"
      >
        <AlertMessages
          v-if="alertValue"
          :alert-type="alertType" 
          :alert-msg="alertMsg"
          @reset="alertValue = false"
        />
        <v-card
          max-width="600"
          elevation="0"
          rounded="xxl"
          color="background"
        > 
          <v-card-title class="text-h5 font-weight-bold text-center justify-center mb-4 no-break">
            {{ $t('eduAdminConnectionTitle') }}
          </v-card-title>
          <v-card-subtitle class="text-body-1 darktext--text no-break" v-html="$t('eduAdminConnectionDescription')">
          </v-card-subtitle>
          <v-card-text>
            <v-form ref="form">
              <v-autocomplete
                v-model="selectedCourseTemplate"
                :menu-props="{
                  rounded: 'xxl',
                  closeOnContentClick:true
                }"
                :items="items"
                hide-selected
                item-value="CourseTemplateId"
                item-text="CourseName"
                :placeholder="$t('eduAdminSearchPlaceholder')"
                return-object
                clearable
                solo
                class="rounded-xxl text-body-2"
                cache-items
                data-testid="course-templates-input"
                prepend-inner-icon="mdi-magnify"
                hide-details="auto"
                @change="changeCourseTemplate()"
              >
                <template v-slot:no-data>
                  <p class="mx-5 mt-4">
                    {{ $t('noDataText') }}
                  </p>
                </template>
              </v-autocomplete>
              <div style="min-height: 150px" class="mt-3">
              <v-list-item-group
                v-if="selectedCourseTemplate && events.length > 0"
                v-model="selectedEvents"
                multiple
                class="px-10"
                data-testid="events-list"
              >
                <v-list-item v-for="(event,index) in events" :key="index" :value="event.EventId" :ripple="false" class="no-hover">
                  <template v-slot:default="{ active }">
                    <v-list-item-action class="mr-1 align-self-start">
                      <v-checkbox :input-value="active" color="accent" data-testid="event-checkbox"></v-checkbox>
                    </v-list-item-action>
                    <v-list-item-content class="text-left">
                      <v-list-item-title class="pre-wrap">{{ event.EventName }} <span class="greytext--text">({{event.EventId}})</span></v-list-item-title>
                    </v-list-item-content>
                  </template>
                </v-list-item>
              </v-list-item-group>
              <div v-if="events.length == 0 && this.selectedCourseTemplate && !loadingEvents"><span v-html="$t('eduAdminNoEventsAvailable')" /></div>
            </div>
            </v-form>
          </v-card-text>
          <v-card-text class="pt-10">
            <div class="d-inline-flex"><v-icon :color="connectionError ? 'error' : 'accent'">mdi-alert</v-icon><span class="ml-1 text-h6" :class="connectionError ? 'error--text' : 'accent--text'" v-html="$t('eduAdminShareActivityOBSTitle')"></span></div>
            <div v-if="connectionError" data-testid="sync-error">
                <span v-html="$t(`eduAdminShareActivityOBSText`+connectionErrorType.replace('_', ''))"></span>
            </div>
            <div v-else>
                <span v-html="$t('eduAdminShareActivityOBSText')"></span>
            </div>
          </v-card-text>
        </v-card>
        <v-row>
          <v-col align="center">
            <FormFooter
              :cancel-button-link="cancelLink"
              cancel-button-text="back"
              next-button-text="save"
              next-button-color="accent"
              :loading="loading || buttonState"
              @proceedSubmit="shareActivity"
            />
          </v-col>
        </v-row>
      </v-col>
    </v-row>          
  </nav>
</template>

<script>
import i18n from '../../plugins/i18n'
import AlertMessages  from '../../components/AlertMessages'
import { getEduAdminCourseTemplates, getCourseTemplateEvents, connectActivityWithEduAdmin, getEduAdminConnections } from '../../api/activityApi'
import { getIntegrations } from "../../api/spaceApi";
import FormFooter  from '../../components/FormFooter'

export default {
    components: {
        AlertMessages,
        FormFooter
    },
    data () {
        return {
            spaceId: window.$cookies.get('space_id'),
            alertMsg:'',
            alertValue:false,
            activityId: this.$route.params.id,
            cancelLink: '/activity/'+this.$route.params.id,
            loading: false,
            loadingData: false,
            loadingEvents: false,
            selectedCourseTemplate: null,
            items: [],
            integrationId: null,
            events: [],
            selectedEvents: [],
            connectionError: false,
            connectionErrorType: null
        }
    },
    beforeRouteEnter(to, from, next) {
        if(from.path != null && from.path != '/redirect' && from.path != '/'){
            next(vm => {
                vm.cancelLink = from.path
            });
        }else { 
            next();
        }
    },

    computed:{
        buttonState: {
            get () {
                if(this.selectedCourseTemplate){
                    if(this.selectedEvents.length > 0){
                        return false
                    }else{
                        return true
                    }
                }else{
                    return false
                }
            }
        }
    },
    async created(){
        this.loadingData = true
        await this.fetchIntegrations()
        await this.fetchCourseTemplates()
        this.loadingData = false
    },
    methods: {
        async fetchIntegrations() {
            try {
                const response = await getIntegrations(this.spaceId)
                if (response.status) {
                    if (response.status == 200) {
                        if (response.data.data.length > 0) {
                            this.integrationId = response.data.data.find(item => item.attributes.name == 'eduadmin' ).id
                        }
                    }
                }
            } catch (e) {
                if (e.response) {
                    if (e.response.status == 404) {
                        this.$router.push({ name: 'PageNotFound' })
                    }
                    else if (e.response.status == 403) {
                        this.$router.push({ name: 'PageAccessDenied' })
                    } else {
                        this.alertValue = true
                        this.alertType = 'error'
                        this.alertMsg = i18n.t('serverError')
                    }
                }
            }
        },
        async fetchCourseTemplates(){
            if(this.integrationId != null){
                try {
                    const response = await getEduAdminCourseTemplates(this.integrationId)
                    if(response.status){
                        if(response.status == 200) {
                            this.items = response.data.meta.events
                            await this.fetchEduAdminConnections()
                        }
                    }
                } catch (e) {
                    if(e.response){
                        if (e.response.status == 400) {
                            for (let i = 0; i < e.response.data.errors.length; i++) {
                                if (
                                    e.response.data.errors[i].title == "invalid_grant"
                                ) {
                                    this.alertValue = true;
                                    this.alertType = "error";
                                    this.alertMsg = i18n.t("eduAdminShareActivityConnectionError");
                                } else {
                                    this.alertValue = true;
                                    this.alertType = "error";
                                    this.alertMsg = i18n.t("serverError");
                                }
                            }
                        }
                        else if(e.response.status == 404) {
                            this.$router.push({name: 'PageNotFound'})
                        }
                        else if(e.response.status == 403){
                            this.$router.push({name: 'PageAccessDenied'})
                        } else {
                            this.alertValue = true
                            this.alertType = 'error'
                            this.alertMsg =  i18n.t('serverError')
                        }
                    }
                }
            }else{
                this.alertValue = true;
                this.alertType = "error";
                this.alertMsg = i18n.t("eduAdminShareActivityConnectionError");
            }
            
        },
        async changeCourseTemplate(){
            this.loadingEvents = true
            this.events = []
            this.selectedEvents = []
            if(this.selectedCourseTemplate){
                try {
                    const response = await getCourseTemplateEvents(this.integrationId, this.selectedCourseTemplate.CourseTemplateId)
                    if(response.status){
                        if(response.status == 200) {
                            this.events = response.data.meta.event.Events
                            this.loadingEvents = false
                        }
                    }
                } catch (e) {
                    if(e.response){
                        if(e.response.status == 404) {
                            this.$router.push({name: 'PageNotFound'})
                        }
                        else if(e.response.status == 403){
                            this.$router.push({name: 'PageAccessDenied'})
                        } else {
                            this.alertValue = true
                            this.alertType = 'error'
                            this.alertMsg =  i18n.t('serverError')
                        }
                    }
                    this.loadingEvents = false
                }
            }
        },
        async fetchEduAdminConnections() {
            try {
                const response = await getEduAdminConnections(this.activityId)
                if (response.status) {
                    if (response.status == 200) {
                        if (response.data.meta.connections.length > 0) {
                            const responseEvent = await getCourseTemplateEvents(this.integrationId, response.data.meta.connections[0].course)
                            this.selectedCourseTemplate = responseEvent.data.meta.event
                            if(this.selectedCourseTemplate != null){
                                this.events = responseEvent.data.meta.event.Events
                                for(let i = 0; i < response.data.meta.connections.length; i++){
                                    if(response.data.meta.connections[i].event != null){
                                        this.selectedEvents.push(parseInt(response.data.meta.connections[i].event));
                                    }
                                }
                            }
                        }
                        if(response.data.meta.has_error){
                            this.connectionError = true
                            this.connectionErrorType = response.data.meta.type
                        }
                    }
                }
            } catch (e) {
                if (e.response) {
                    if (e.response.status == 404) {
                        this.$router.push({ name: 'PageNotFound' })
                    }
                    else if (e.response.status == 403) {
                        this.$router.push({ name: 'PageAccessDenied' })
                    } else {
                        this.alertValue = true
                        this.alertType = 'error'
                        this.alertMsg = i18n.t('serverError')
                    }
                }
            }
            
        },
        async shareActivity(){
            this.loading = true
            try {
                let data = {
                    data: {
                        type: "activity-connections",
                        attributes: {
                            course: this.selectedCourseTemplate == null ? null : this.selectedCourseTemplate.CourseTemplateId,
                            events: this.selectedEvents
                        }
                    }
                }
                const response = await connectActivityWithEduAdmin(this.activityId, data)
                if(response.status){
                    if(response.status == 200) {
                        this.$router.push({name: 'ViewActivityPage', params: {id: this.$route.params.id, successfullyConnectedActivityEduAdmin: true}})
                    }
                }
            } catch (e) {
                if(e.response){
                    if(e.response.status == 404) {
                        this.$router.push({name: 'PageNotFound'})
                    }
                    else if(e.response.status == 403){
                        this.$router.push({name: 'PageAccessDenied'})
                    } else {
                        this.alertValue = true
                        this.alertType = 'error'
                        this.alertMsg =  i18n.t('serverError')
                    }
                    this.loading = false
                }
            }
        }
    },
}

</script>